import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/di/container';
import { QUERY_KEYS } from '@shared/constants';
import type { CreateFolderPayload, UpdateFolderPayload } from '@core/repositories/FolderRepository';

export const useFolders = (collectionId: string | undefined, parentId?: string | null) => {
  return useQuery({
    queryKey: QUERY_KEYS.folders(collectionId ?? '', parentId),
    queryFn: () => container.folders.list(collectionId as string, parentId),
    enabled: Boolean(collectionId),
  });
};

export const useFolder = (id: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.folder(id ?? ''),
    queryFn: () => container.folders.get(id as string),
    enabled: Boolean(id),
  });
};

export const useCreateFolder = (collectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateFolderPayload) => container.folders.create(collectionId, payload),
    onSuccess: (folder) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.folders(collectionId, folder.parentId),
      });
    },
  });
};

export const useUpdateFolder = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateFolderPayload) => container.folders.update(id, payload),
    onSuccess: (folder) => {
      queryClient.setQueryData(QUERY_KEYS.folder(id), folder);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.folders(folder.collectionId, folder.parentId),
      });
    },
  });
};

export const useMoveFolder = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ parentId, position }: { parentId: string | null; position: number }) =>
      container.folders.move(id, parentId, position),
    onSuccess: (folder) => {
      queryClient.setQueryData(QUERY_KEYS.folder(id), folder);
      queryClient.invalidateQueries({ queryKey: ['collections', folder.collectionId, 'folders'] });
    },
  });
};

export const useDeleteFolder = (collectionId: string, parentId?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.folders.remove(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.folders(collectionId, parentId) }),
  });
};

export const useToggleFolderFavorite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isFavorited }: { id: string; isFavorited: boolean }) =>
      isFavorited ? container.folders.unfavorite(id) : container.folders.favorite(id),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.folder(id) });
    },
  });
};
